'use client';
import { useEffect, useState } from 'react';
import { isRetryPolicy, type RetryAttemptContext, type RetryAttemptResult, type RetryController, type RetryPhase } from 'elowen-plugin-shared/retry';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from './queries';
import { BASE, acceptConnectionPolicy, getConnectionPolicy, loadConnectionPolicy } from './elowenClient';
import { readDaemonStream } from './daemonStream';
import { AUTH_CLEARED_EVENT, authGeneration, subscribeAuthTransitions } from './token';
import { DEFAULT_STREAM_SILENCE, startStreamWatchdog } from './streamWatchdog';
import { createReconnectController } from './reconnect';
import { STALE_HIDE_MS, subscribeRevive } from './useRevive';
import { emitPluginEvent, isPluginEvent } from './pluginEvents';
import { createModuleEmitter } from './moduleEmitter';
import { emitVoiceRingEvent, type VoiceRingEvent } from './voiceEvents';
import { ENVIRONMENT_OPERATION_EVENT } from './useEnvironmentOperation';

const retryPolicyEventSchema = z.object({ type: z.literal('retry-policy'), policy: z.custom(isRetryPolicy) });
const computerConnectionSchema = z.object({
  type: z.literal('computer-connection'), projectId: z.number().int().positive(), targetId: z.string().min(1),
  authorityId: z.uuid(), generation: z.number().int().nonnegative(), connected: z.boolean(),
  sequence: z.number().int().nonnegative(), observedAt: z.number().int().nonnegative(),
});
/** The fields of a `/brain/conversations` frame the browser acts on (`BrainConversationEvent` per update). */
const conversationsFrameSchema = z.object({
  updates: z.array(z.object({ sessionId: z.string().min(1), removed: z.literal(true).optional() })),
});

/** The signed-in account lost access to one of its conversations: the owner removed it, it left, or the
 *  owner deleted the conversation. The daemon marks `removed` only on the account's own update, never on an
 *  administrator's oversight copy, so a viewer reading someone else's conversation is never told this. */
export interface ConversationRemoval { sessionId: string }
const conversationRemovals = createModuleEmitter<ConversationRemoval>();
export const subscribeConversationRemovals = conversationRemovals.subscribe;

/** Subscribe to the core daemon SSE bus and keep shared query caches fresh. */
export function useElowenEvents(): void {
  const qc = useQueryClient();
  const [failure, setFailure] = useState<unknown>(null);
  useEffect(() => {
    const issuedIn = authGeneration();
    const lifetime = new AbortController();
    const cancel = () => lifetime.abort();
    const offAuth = subscribeAuthTransitions(cancel);
    window.addEventListener(AUTH_CLEARED_EVENT, cancel);
    let reconnect: RetryController<void> | undefined;
    let conversationsReconnect: RetryController<void> | undefined;
    let visible = !document.hidden;
    let corePhase: RetryPhase | null = null;
    let conversationsPhase: RetryPhase | null = null;
    let activeCore: { abort: AbortController; lastBytesAt: number; presenceChange: boolean; context: RetryAttemptContext } | undefined;
    let activeConversations: { abort: AbortController; lastBytesAt: number; presenceChange: boolean; context: RetryAttemptContext } | undefined;
    const current = () => !lifetime.signal.aborted && issuedIn === authGeneration();

    const makeHandler = (invalidate: () => void) => (event: { data: string }) => {
      try { JSON.parse(event.data); } catch { return; }
      invalidate();
    };

    // Invalidation alone reuses an initial fetch with no cached data. Retire that pre-event read
    // before refreshing so its late idle answer cannot hide the operation that just started.
    const refreshUpdateStatus = async () => {
      await qc.cancelQueries({ queryKey: QUERY_KEYS.systemUpdate });
      if (!current()) return;
      await qc.invalidateQueries({ queryKey: QUERY_KEYS.systemUpdate });
    };
    const refreshMemoryMaintenance = async () => {
      await qc.cancelQueries({ queryKey: QUERY_KEYS.memoryMaintenance });
      if (!current()) return;
      await qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryMaintenance });
    };

    const refreshMemoryDreaming = async () => {
      await qc.cancelQueries({ queryKey: QUERY_KEYS.memoryDreaming });
      if (!current()) return;
      await qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryDreaming });
    };

    const memoryHandler = makeHandler(() => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.memories });
      qc.invalidateQueries({ queryKey: ['memory-vitality'] });
      // A finished memory-maintenance pass changes stored memories and reports its own outcome through
      // the same event, so the account card's overview re-reads its runs with the list.
      void refreshMemoryDreaming();
      void refreshMemoryMaintenance();
    });
    const refreshAlertKinds = async () => {
      await qc.cancelQueries({ queryKey: ['my-alert-kinds'] });
      if (current()) await qc.invalidateQueries({ queryKey: ['my-alert-kinds'] });
    };
    const pluginsHandler = makeHandler(() => {
      void refreshAlertKinds();
      qc.invalidateQueries({ queryKey: QUERY_KEYS.pluginUi });
      qc.invalidateQueries({ queryKey: QUERY_KEYS.platformMetadata });
      qc.invalidateQueries({ queryKey: ['plugins'] });
      qc.invalidateQueries({ queryKey: ['marketplace'] });
      qc.invalidateQueries({ queryKey: QUERY_KEYS.brainCommands });
    });
    const pluginHandler = (event: { data: string }) => {
      let parsed: unknown;
      try { parsed = JSON.parse(event.data); } catch { return; }
      // The payload goes to whoever is listening for it BEFORE any invalidation, so a surface that
      // follows a pushed operation reads the frame it was sent rather than refetching it.
      if (isPluginEvent(parsed)) emitPluginEvent(parsed);
      // A lifecycle operation publishes a frame per step — up to four a second while an image builds —
      // and every one of them used to refresh the WHOLE cache, which cost more than the poll the push
      // replaced. Such a frame names the project it belongs to, so it refreshes what names the same
      // project plus the project list, without core learning any bundle's private key convention.
      if (isPluginEvent(parsed) && parsed.kind === ENVIRONMENT_OPERATION_EVENT && typeof parsed.projectId === 'number') {
        const { projectId } = parsed;
        qc.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'environment-operations' && query.queryKey.includes(projectId) });
        qc.invalidateQueries({ queryKey: ['projects'] });
        return;
      }
      // Every other plugin's data is intentionally opaque to core: invalidate active queries so the
      // owning bundle refreshes whatever it keeps.
      if (isPluginEvent(parsed) && qc.getQueryCache().findAll({
        queryKey: ['plugin-dock-operations', parsed.plugin, parsed.kind],
      }).length > 0) {
        qc.invalidateQueries({ predicate: query => query.queryKey[0] !== 'plugin-dock-operations' });
      } else qc.invalidateQueries();
    };
    const voiceRingHandler = (type: VoiceRingEvent['type']) => (event: { data: string }) => {
      let data: unknown;
      try { data = JSON.parse(event.data); }
      catch { data = null; } // Let the controller show its explicit malformed-ring error.
      emitVoiceRingEvent({ type, data });
    };
    const activityHandler = makeHandler(() => {
      qc.invalidateQueries({ queryKey: ['activity'] });
      qc.invalidateQueries({ queryKey: ['activity-presence'] }); // someone started working
      qc.invalidateQueries({ queryKey: ['activity-pulse'] });
    });
    const authHandler = makeHandler(() => {
      void refreshAlertKinds();
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
    });
    // The conversation list's live channel. It names the OWNER's own conversations, and an administrator
    // also receives the invalidations of the conversations its All list shows, so both lists re-read: the
    // row carries the live measurement of a working conversation wherever that conversation runs.
    //
    // A turn settles once per provider response, so one working conversation produces a burst of
    // invalidations. Each list therefore keeps ONE read in flight and reads once more when a change
    // landed while that read was running: twenty settles cost two reads, not twenty. The read itself is
    // not cancelled when it is superseded, so the list never waits for a request it no longer wants.
    const reading = new Map<string, Promise<void>>();
    const changed = new Set<string>();
    const refreshList = (key: 'brain-sessions' | 'brain-managed-sessions'): Promise<void> => {
      const current = reading.get(key);
      if (current) { changed.add(key); return current; }
      const read = qc.invalidateQueries({ queryKey: [key] }, { cancelRefetch: false })
        .catch(() => undefined)
        .then(() => {
          reading.delete(key);
          if (!changed.delete(key)) return;
          return refreshList(key);
        });
      reading.set(key, read);
      return read;
    };
    const conversationsHandler = (event: { data: string }) => {
      let data: unknown;
      try { data = JSON.parse(event.data); } catch { return; }
      const { updates } = conversationsFrameSchema.parse(data);
      void refreshList('brain-sessions');
      void refreshList('brain-managed-sessions');
      // The frame is already fenced to this account's live feed (`current()` in `onFrame`).
      for (const update of updates) if (update.removed) conversationRemovals.emit({ sessionId: update.sessionId });
    };
    // Content-free per-recipient bell invalidation: the event names the user and the row, never the
    // alert's text, so the panel simply refetches. Deliveries are already user-scoped server-side.
    const alertsHandler = makeHandler(() => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.alerts });
    });
    // The daemon announces every change of an account's usage it lets this user see; the map is re-read
    // rather than patched, because the frame carries no figures. A first read still in flight may predate
    // the reading (an empty map right after boot), and invalidation alone would join it, so retire it first.
    let rateLimitsReading: Promise<void> | undefined;
    let rateLimitsChanged = false;
    const refreshRateLimits = (): Promise<void> => {
      if (rateLimitsReading) { rateLimitsChanged = true; return rateLimitsReading; }
      const read = (async () => {
        await qc.cancelQueries({ queryKey: QUERY_KEYS.brainRateLimits });
        if (!current()) return;
        await qc.invalidateQueries({ queryKey: QUERY_KEYS.brainRateLimits });
      })().finally(() => {
        rateLimitsReading = undefined;
        // A burst of charged steps changes one all-accounts map. Finish this read, then sample once
        // more rather than cancelling and restarting it for every charge (including other accounts).
        if (!rateLimitsChanged || !current()) return;
        rateLimitsChanged = false;
        return refreshRateLimits();
      });
      rateLimitsReading = read;
      return read;
    };
    const providerUsageHandler = makeHandler(() => { void refreshRateLimits(); });

    const handlers: Record<string, (event: { data: string }) => void> = {
      memory: memoryHandler, update: makeHandler(refreshUpdateStatus), plugin: pluginHandler,
      voice_ring: voiceRingHandler('voice_ring'), voice_ring_end: voiceRingHandler('voice_ring_end'),
      plugins: pluginsHandler, activity: activityHandler, alert: alertsHandler,
      'provider-usage': providerUsageHandler, auth: authHandler,
      'user-presence': makeHandler(() => qc.invalidateQueries({ queryKey: ['users'] })),
    };
    const hydrateCore = () => Promise.all([
      qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }),
      refreshMemoryMaintenance(), refreshMemoryDreaming(),
      qc.invalidateQueries({ queryKey: QUERY_KEYS.pluginUi }),
      qc.invalidateQueries({ queryKey: QUERY_KEYS.platformMetadata }),
      qc.invalidateQueries({ queryKey: QUERY_KEYS.brainCommands }),
      qc.invalidateQueries({ queryKey: ['plugins'] }),
      qc.invalidateQueries({ queryKey: ['activity'] }),
      qc.invalidateQueries({ queryKey: ['activity-presence'] }),
      qc.invalidateQueries({ queryKey: ['activity-pulse'] }),
      qc.invalidateQueries({ queryKey: ['users'] }),
      qc.invalidateQueries({ queryKey: ['projects'] }),
      qc.invalidateQueries({ queryKey: QUERY_KEYS.environmentOperations }),
      qc.invalidateQueries({ queryKey: ['plugin-dock-operations'] }),
      refreshRateLimits(), refreshUpdateStatus(), refreshAlertKinds(),
    ]);

    const reportFailure = (error: unknown) => { if (current()) setFailure(error); };
    const runFeed = async (context: RetryAttemptContext, core: boolean): Promise<RetryAttemptResult<void>> => {
      // A presence-mode replacement is not a failed connection or a new retry episode.
      do {
        const active = { abort: new AbortController(), lastBytesAt: Date.now(), presenceChange: false, context };
        const computers = new Map<string, z.infer<typeof computerConnectionSchema>>();
        if (core) activeCore = active; else activeConversations = active;
        const signal = AbortSignal.any([context.signal, active.abort.signal]);
        let hydration: Promise<unknown> | undefined;
        const hydrate = () => {
          if (hydration) return;
          // The conversation feed replays nothing it missed while down, so both lists it moves re-read
          // here, the administrator's All list included.
          hydration = (core ? hydrateCore() : Promise.all([refreshList('brain-sessions'), refreshList('brain-managed-sessions')]))
            .then(() => { if (current() && !signal.aborted) context.ready(); });
          void hydration.catch(error => {
            if (current() && !signal.aborted) reportFailure(error);
            active.abort.abort();
          });
        };
        const stopWatchdog = startStreamWatchdog({
          lastFrameAt: () => active.lastBytesAt,
          onSilent: () => active.abort.abort(),
        });
        try {
          const result = await readDaemonStream(
            core ? `${BASE}/events${document.hidden ? '?presence=0' : ''}` : `${BASE}/brain/conversations`,
            {
              signal,
              onBytes: () => { active.lastBytesAt = Date.now(); },
              onKeepalive: () => { if (current() && !signal.aborted) hydrate(); },
              onFrame: async (frame) => {
                if (!current() || signal.aborted) return;
                if (core && frame.event === 'retry-policy') {
                  const event = retryPolicyEventSchema.parse(JSON.parse(frame.data) as unknown);
                  acceptConnectionPolicy(event.policy, issuedIn);
                } else if (core && frame.event === 'computer-connection') {
                  const event = computerConnectionSchema.parse(JSON.parse(frame.data) as unknown);
                  const key = `${event.projectId}:${event.targetId}`;
                  const previous = computers.get(key);
                  // One fetch belongs to one daemon authority. A new fetch refreshes the read model
                  // and starts a new observation epoch; an old socket never regains authority here.
                  if (previous && (previous.authorityId !== event.authorityId || event.generation < previous.generation
                    || event.sequence <= previous.sequence)) return;
                  computers.set(key, event);
                  await qc.cancelQueries({ queryKey: ['projects'] });
                  if (!current() || signal.aborted) return;
                  await qc.invalidateQueries({ queryKey: ['projects'] });
                } else if (core) handlers[frame.event]?.(frame);
                else if (frame.event === 'conversations') conversationsHandler(frame);
                // Malformed data may reach a domain's explicit error presentation, but it is not a handshake.
                try { JSON.parse(frame.data); } catch { return; }
                hydrate();
              },
            },
          );
          if (!current() || context.signal.aborted) return { outcome: 'cancelled', reason: 'aborted' };
          if (active.presenceChange) continue;
          if (active.abort.signal.aborted) return { outcome: 'retry', reason: 'timeout' };
          return result;
        } finally {
          stopWatchdog();
          if (core) activeCore = undefined; else activeConversations = undefined;
        }
      } while (current() && !context.signal.aborted);
      return { outcome: 'cancelled', reason: 'aborted' };
    };
    const observe = (controller: RetryController<void>, restart = false) => {
      const episode = restart ? controller.restart() : controller.start();
      void episode.done.catch(reportFailure);
    };
    void loadConnectionPolicy(lifetime.signal).then(() => {
      if (!current()) return;
      reconnect = createReconnectController({
        target: { kind: 'events', id: 'global' }, getPolicy: getConnectionPolicy,
        signal: lifetime.signal, attempt: context => runFeed(context, true),
        onProgress: progress => { corePhase = progress.phase; },
      });
      conversationsReconnect = createReconnectController({
        target: { kind: 'events', id: 'conversations' }, getPolicy: getConnectionPolicy,
        signal: lifetime.signal, attempt: context => runFeed(context, false),
        onProgress: progress => { conversationsPhase = progress.phase; },
      });
      observe(reconnect);
      observe(conversationsReconnect);
    }).catch(reportFailure);

    const onVisibility = () => {
      if (visible === !document.hidden) return;
      visible = !document.hidden;
      if (activeCore && !activeCore.abort.signal.aborted) {
        activeCore.context.notReady();
        activeCore.presenceChange = true;
        activeCore.abort.abort();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    const offRevive = subscribeRevive(({ hiddenMs, networkRecovered, foregroundReturned }) => {
      if (!current()) return;
      for (const [controller, active, phase] of [
        [reconnect, activeCore, corePhase],
        [conversationsReconnect, activeConversations, conversationsPhase],
      ] as const) {
        if (!controller) continue;
        const silent = active && Date.now() - active.lastBytesAt > DEFAULT_STREAM_SILENCE.reviveLimitMs;
        const stale = silent || hiddenMs > STALE_HIDE_MS;
        const renewable = phase === 'exhausted' || (stale && (phase === 'attempting' || phase === 'scheduled' || phase === 'succeeded'));
        if (networkRecovered || (foregroundReturned && renewable)) {
          observe(controller, true);
        } else {
          if (silent) active.abort.abort();
          observe(controller);
        }
      }
    });

    return () => {
      cancel();
      offAuth();
      window.removeEventListener(AUTH_CLEARED_EVENT, cancel);
      offRevive();
      document.removeEventListener('visibilitychange', onVisibility);
      void reconnect?.stop();
      void conversationsReconnect?.stop();
    };
  }, [qc]);
  if (failure) throw failure;
}
